import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import EmailResourceMetadata from "@mapmycustomers/shared/types/email/EmailResourceMetadata";
import Resource from "@mapmycustomers/shared/types/entity/Resource";
import useBoolean from "@mapmycustomers/shared/util/hook/useBoolean";
import useDynamicCallback from "@mapmycustomers/shared/util/hook/useDynamicCallback";
import { EmailBodyEditor, EmailBodyProps } from "@mapmycustomers/ui";

import EmailResourceMakePublicModal from "@app/component/email/EmailResourceMakePublicModal";
import EmailResourceUnableToInsertModal from "@app/component/email/EmailResourceUnableToInsertModal";
import ResourcesSelectModal, {
  ResourcesSelectModalProps,
} from "@app/component/ResourcesSelectModal";
import ResourceType from "@app/enum/ResourceType";
import { downloadFile } from "@app/store/files/actions";
import { getCurrentUserId } from "@app/store/iam";
import { useExtendedAnalytics } from "@app/util/analytic/AnalyticsService";
import useAnalytics from "@app/util/contexts/useAnalytics";

import AnalyticsContext from "../contexts/AnalyticsContext";

const RESOURCE_TYPES = [ResourceType.FILE];

const useEmailResources = ({
  editor,
  resources,
  resourcesMetadata,
}: {
  editor?: EmailBodyEditor;
  resources?: Resource[];
  resourcesMetadata?: EmailResourceMetadata[];
} = {}) => {
  const dispatch = useDispatch();
  const currentUserId = useSelector(getCurrentUserId);

  const addResourceAnalytics = useExtendedAnalytics("Add Resource", useAnalytics());

  const [resourcesMap, setResourcesMap] = useState<ReadonlyMap<Resource["id"], Resource>>(
    () => new Map()
  );

  const [resourcesMetadataMap, setResourcesMetadataMap] = useState<
    ReadonlyMap<Resource["id"], EmailResourceMetadata>
  >(() => new Map());

  const [resourceSelectModalVisible, showResourceSelectModal, hideResourceSelectModal] =
    useBoolean(false);

  const [selectedNonPublicResourceId, setSelectedNonPublicResourceId] = useState<
    Resource["id"] | undefined
  >();

  const [
    unableToInsertResourceLinkModalVisible,
    showUnableToInsertResourceLinkModal,
    hideUnableToInsertResourceLinkModal,
  ] = useBoolean();

  const updateResources = useDynamicCallback(
    (updateCallback: (resources: Resource[]) => Resource[]) => {
      setResourcesMap((resources) => {
        const updatedResources = updateCallback(Array.from(resources.values()));
        return new Map(updatedResources.map((resource) => [resource.id, resource]));
      });
    }
  );

  const insertResourceLink = useDynamicCallback((insertingResource: Resource) => {
    updateResources((resources) =>
      resources.filter((resource) => resource.id !== insertingResource.id).concat(insertingResource)
    );
    editor?.insertResourceLink(insertingResource.id);
    addResourceAnalytics.completed("Insert");
  });

  useEffect(() => {
    setResourcesMetadataMap(
      new Map(
        resourcesMetadata?.map((resourceMetadata) => [resourceMetadata.id, resourceMetadata]) ?? []
      )
    );
  }, [resourcesMetadata]);

  const handleResourceDownload = useDynamicCallback<
    NonNullable<EmailBodyProps["onResourceDownload"]>
  >((resourceId) => {
    const resource = resourcesMap.get(resourceId);
    if (resource?.file) {
      dispatch(downloadFile({ fileId: resource.file.id, fileName: resource.name }));
    }
  });

  const resourceGetter = useDynamicCallback<NonNullable<EmailBodyProps["resourceGetter"]>>(
    (resourceId) => {
      return resourcesMap.get(resourceId);
    }
  );

  const handleResourcesChange = useDynamicCallback((resources: Resource[]) => {
    setResourcesMap(new Map(resources?.map((resource) => [resource.id, resource]) ?? []));
  });

  const resourceMetadataGetter = useDynamicCallback<
    NonNullable<EmailBodyProps["resourceMetadataGetter"]>
  >((resourceId) => {
    return resourcesMetadataMap.get(resourceId);
  });

  const handleResourceLinksChange = useDynamicCallback<
    NonNullable<EmailBodyProps["onResourceLinksChange"]>
  >((resourceIds) => {
    updateResources((resources) =>
      resources.filter((resource) => resourceIds.includes(resource.id))
    );
  });

  const handleResourceLinkInsert = useDynamicCallback<
    NonNullable<EmailBodyProps["onResourceLinkInsert"]>
  >(() => {
    showResourceSelectModal();
    addResourceAnalytics.initiated();
  });

  const handleResourceSelectConfirm = useDynamicCallback<ResourcesSelectModalProps["onConfirm"]>(
    (resources) => {
      const resource = resources[0];
      if (!resource.isPublic) {
        if (resource.userId === currentUserId) {
          setSelectedNonPublicResourceId(resource.id);
          hideResourceSelectModal();
        } else {
          showUnableToInsertResourceLinkModal();
        }
      } else {
        insertResourceLink(resource);
        hideResourceSelectModal();
      }
    }
  );

  const handleResourceMadePublic = useDynamicCallback((resource: Resource) => {
    setSelectedNonPublicResourceId(undefined);
    insertResourceLink(resource);
  });

  const handleResourceMakePublicCancel = useDynamicCallback(() => {
    setSelectedNonPublicResourceId(undefined);
    hideResourceSelectModal();
    addResourceAnalytics.cancelled();
  });

  const handleUnableToInsertResourceLinkModalOk = useDynamicCallback(() => {
    hideUnableToInsertResourceLinkModal();
  });

  const handleUnableToInsertResourceLinkModalCancel = useDynamicCallback(() => {
    hideUnableToInsertResourceLinkModal();
    hideResourceSelectModal();
    addResourceAnalytics.cancelled();
  });

  const handleResourcesSelectModalCancel = useDynamicCallback(() => {
    hideResourceSelectModal();
    addResourceAnalytics.cancelled();
  });

  useEffect(() => {
    handleResourcesChange(resources ?? []);
  }, [resources, handleResourcesChange]);

  const renderResourcesModals = () => {
    return (
      <AnalyticsContext.Provider value={addResourceAnalytics}>
        {resourceSelectModalVisible && (
          <ResourcesSelectModal
            onCancel={handleResourcesSelectModalCancel}
            onConfirm={handleResourceSelectConfirm}
            open
            resourceTypes={RESOURCE_TYPES}
          />
        )}
        {selectedNonPublicResourceId !== undefined && (
          <EmailResourceMakePublicModal
            onCancel={handleResourceMakePublicCancel}
            onResourceUpdated={handleResourceMadePublic}
            resourceId={selectedNonPublicResourceId}
          />
        )}
        {unableToInsertResourceLinkModalVisible && (
          <EmailResourceUnableToInsertModal
            onCancel={handleUnableToInsertResourceLinkModalCancel}
            onOk={handleUnableToInsertResourceLinkModalOk}
          />
        )}
      </AnalyticsContext.Provider>
    );
  };

  return {
    onResourceDownload: handleResourceDownload,
    onResourceLinkInsert: handleResourceLinkInsert,
    onResourceLinksChange: handleResourceLinksChange,
    onResourcesChange: handleResourcesChange,
    renderResourcesModals,
    resourceGetter,
    resourceMetadataGetter,
    resources: resourcesMap,
  };
};

export default useEmailResources;
